/**
 * Eine Unterlage für die KI vorbereiten — Text zuerst (Betreiber 17.10.2026).
 *
 * Das PDF wird im Browser über seine Textebene (pdf.js) in Seitentext
 * gewandelt; an die KI geht nur dieser Text. Das ist kleiner und schneller,
 * und vor allem lässt sich das „wörtliche Zitat" der KI gegen genau diesen
 * Text PRÜFEN (`zitat.ts`). Das Original geht nur, wenn die Textebene fehlt
 * oder zu dünn ist (Scan, gescannte Bescheide, Pläne) — dann ist das Zitat
 * nicht prüfbar, und die Auswertung lässt nur Ja-Vorschläge durch.
 *
 * Seiten ohne genug Text (Scanseite, Plan, Stempelblatt) gehen im Textweg
 * NICHT mit; sie stehen in `ohneTextSeiten`, damit niemand glaubt, die KI
 * habe sie gelesen.
 *
 * Grenze: Tabellen und Layout gehen in der reinen Textebene teilweise
 * verloren; Zeilen werden wie im LV-Markiermodus gruppiert (`pdfTextLines`).
 */

import { extractPdfPages, hasTextLayer, type PdfPageText } from '../pdf/pdfTextExtract';
import { groupPageLines } from '../pdf/pdfTextLines';
import { KI_MAX_DATEI_BYTES } from './auswertung';
import type { KiQuellDatei, KiQuelle } from './typen';

/** Darunter gilt eine Seite als „ohne Text" (Zeichen ohne Leerraum) und geht nicht als Text. */
export const TEXT_MIN_ZEICHEN_JE_SEITE = 150;
/** Darunter ist das ganze Dokument zu dünn, egal wie viele Seiten. */
export const TEXT_MIN_ZEICHEN_GESAMT = 300;
/** Höchstanteil von Seiten ohne Text, bei dem der Textweg noch reicht. */
export const TEXT_MAX_ANTEIL_DUENN = 1 / 3;

const zeichenOhneLeerraum = (text: string): number => text.replace(/\s/g, '').length;

/** Seitentext aus der pdf.js-Textebene, je Seite mit echter Nummer (auch leere Seiten). */
export const seitenAusPdfText = (pages: PdfPageText[]): { seite: number; text: string }[] =>
    pages.map((p, i) => ({
        seite: i + 1,
        text: groupPageLines(p.items, i).map((l) => l.text).join('\n'),
    }));

/** Teilt in Seiten mit Text und Seiten ohne (genug) Text. */
export const teileSeiten = (
    seiten: { seite: number; text: string }[],
): { mitText: { seite: number; text: string }[]; ohneText: number[] } => {
    const mitText: { seite: number; text: string }[] = [];
    const ohneText: number[] = [];
    for (const s of seiten) {
        if (zeichenOhneLeerraum(s.text) >= TEXT_MIN_ZEICHEN_JE_SEITE) mitText.push(s);
        else ohneText.push(s.seite);
    }
    return { mitText, ohneText };
};

/**
 * Reicht die Textebene? Nein, wenn insgesamt kaum Text da ist oder mehr als
 * ein Drittel der Seiten ohne Text ist — dann geht besser das ganze PDF.
 */
export const textebeneReicht = (seiten: { seite: number; text: string }[], seitenzahl: number): boolean => {
    if (seitenzahl <= 0) return false;
    const { mitText } = teileSeiten(seiten);
    const gesamt = mitText.reduce((s, x) => s + zeichenOhneLeerraum(x.text), 0);
    if (gesamt < TEXT_MIN_ZEICHEN_GESAMT) return false;
    return (seitenzahl - mitText.length) / seitenzahl <= TEXT_MAX_ANTEIL_DUENN;
};

/** Dateiart aus den ersten Bytes — die Dateiendung der Ablage ist kein Beleg. */
export const erkenneDateiart = (bytes: Uint8Array): KiQuellDatei['mimeType'] | null => {
    const b = (i: number) => bytes[i];
    // "%PDF-" darf nach Vorspann stehen; pdf.js sucht in den ersten 1024 Bytes.
    const kopf = String.fromCharCode(...bytes.subarray(0, Math.min(bytes.length, 1024)));
    if (kopf.includes('%PDF-')) return 'application/pdf';
    if (b(0) === 0x89 && b(1) === 0x50 && b(2) === 0x4e && b(3) === 0x47) return 'image/png';
    if (b(0) === 0xff && b(1) === 0xd8 && b(2) === 0xff) return 'image/jpeg';
    if (kopf.startsWith('RIFF') && kopf.slice(8, 12) === 'WEBP') return 'image/webp';
    return null;
};

/**
 * Bereitet EINE vom Nutzer ausgewählte Datei vor. Wirft nur, wenn die Datei
 * weder PDF noch Bild ist. Ist die Textebene nicht lesbar (beschädigt,
 * verschlüsselt), geht das Original an die KI — sie scheitert dann sichtbar
 * statt still.
 */
export const bereiteQuelleVor = async (bytes: Uint8Array, dokument: string): Promise<KiQuelle> => {
    const mimeType = erkenneDateiart(bytes);
    if (!mimeType) {
        throw new Error(`„${dokument}" ist weder PDF noch Bild — die KI liest nur PDF, PNG, JPEG und WebP.`);
    }
    // Das Original geht nur als Scan an die KI. Größe VOR der Kopie prüfen:
    // eine 40-MB-Datei wird sonst kopiert, nur um danach verworfen zu werden.
    const alsDatei = (seitenGesamt: number): KiQuelle => {
        if (bytes.length > KI_MAX_DATEI_BYTES) {
            return {
                dokument, art: 'pdf', seiten: [], seitenGesamt, ohneTextSeiten: [],
                verworfen: 'ohne lesbare Textebene und zu groß für die KI (höchstens 13 MB)',
            };
        }
        // Kopie: pdf.js und spätere Uploads dürfen sich den Puffer nicht teilen.
        return { dokument, art: 'pdf', seiten: [], seitenGesamt, ohneTextSeiten: [], datei: { daten: bytes.slice(), mimeType } };
    };
    if (mimeType !== 'application/pdf') return alsDatei(1);

    let pages: PdfPageText[];
    try {
        pages = await extractPdfPages(bytes);
    } catch (e) {
        console.warn(`Textebene von „${dokument}" nicht lesbar — das PDF selbst geht an die KI.`, e);
        return alsDatei(0);
    }
    if (!hasTextLayer(pages)) return alsDatei(pages.length);
    const alle = seitenAusPdfText(pages);
    if (!textebeneReicht(alle, pages.length)) return alsDatei(pages.length);
    const { mitText, ohneText } = teileSeiten(alle);
    return { dokument, art: 'text', seiten: mitText, seitenGesamt: pages.length, ohneTextSeiten: ohneText };
};
